'use client';

import { useRef, useEffect, useMemo } from 'react';
import type { SearchResult } from '@/lib/search-client';
import { PAGE_TYPE_ORDER, PAGE_TYPE_GROUP_LABELS } from '@/lib/search-categories';
import { ui, uiFb } from '@/lib/i18n';
import { CommandPaletteResultItem } from './CommandPaletteResultItem';

interface CommandPaletteResultsProps {
  results: SearchResult[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (result: SearchResult) => void;
  query: string;
  lang: string;
}

interface GroupedItem {
  result: SearchResult;
  flatIndex: number;
  isFirstInGroup: boolean;
  groupLabel: string;
}

export function CommandPaletteResults({
  results,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  query,
  lang,
}: CommandPaletteResultsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Group results by page type in display order
  const grouped = useMemo(() => {
    const byType = new Map<string, SearchResult[]>();
    for (const r of results) {
      const list = byType.get(r.pageType) ?? [];
      list.push(r);
      byType.set(r.pageType, list);
    }

    const items: GroupedItem[] = [];
    let flatIndex = 0;

    // Ordered groups first
    for (const type of PAGE_TYPE_ORDER) {
      const group = byType.get(type);
      if (!group) continue;
      const phraseKey = PAGE_TYPE_GROUP_LABELS[type] || type;
      const label = ui(phraseKey, lang);
      const displayLabel = label === phraseKey ? type.replace(/-/g, ' ') : label;

      for (let i = 0; i < group.length; i++) {
        items.push({
          result: group[i],
          flatIndex,
          isFirstInGroup: i === 0,
          groupLabel: displayLabel,
        });
        flatIndex++;
      }
    }

    // Any remaining types not in PAGE_TYPE_ORDER
    for (const [type, group] of byType) {
      if (PAGE_TYPE_ORDER.includes(type)) continue;
      for (let i = 0; i < group.length; i++) {
        items.push({
          result: group[i],
          flatIndex,
          isFirstInGroup: i === 0,
          groupLabel: type.replace(/-/g, ' '),
        });
        flatIndex++;
      }
    }

    return items;
  }, [results, lang]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Result count announcement (i18n)
  const announcement = query.length >= 2
    ? uiFb('ui.search.result_announcement', '{count} results for {query}', lang, {
        count: results.length,
        query,
      })
    : '';

  return (
    <>
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
      <div
        ref={listRef}
        id="command-palette-results"
        role="listbox"
        aria-label={uiFb('ui.search.results_label', 'Search results', lang)}
        className="max-h-[min(60vh,400px)] overflow-y-auto p-2"
      >
        {grouped.map((item) => (
          <div key={item.result.href + item.flatIndex}>
            {item.isFirstInGroup && (
              <div
                id={`search-group-${item.result.pageType}`}
                role="separator"
                aria-label={item.groupLabel}
                className="px-3 pb-1 pt-2.5 text-caption font-semibold uppercase tracking-wider text-text-dim first:pt-1"
              >
                {item.groupLabel}
              </div>
            )}
            <CommandPaletteResultItem
              result={item.result}
              isActive={item.flatIndex === activeIndex}
              index={item.flatIndex}
              onSelect={onSelect}
              onHover={onActiveIndexChange}
            />
          </div>
        ))}
      </div>
    </>
  );
}
